"use client";

import { useEffect, useRef, useState } from "react";
import { motion, useReducedMotion } from "motion/react";
import { useTranslations } from "next-intl";
import { Icon } from "@iconify/react";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";

const CHOICES = [
    { id: "rock", emoji: "✊", labelKey: "Js_rps_rock" },
    { id: "paper", emoji: "✋", labelKey: "Js_rps_paper" },
    { id: "scissors", emoji: "✌️", labelKey: "Js_rps_scissors" },
];

// Ce que chaque coup bat.
const BEATS = { rock: "scissors", paper: "rock", scissors: "paper" };

const EMOJI = { rock: "✊", paper: "✋", scissors: "✌️" };

function compute(you, cpu) {
    if (you === cpu) return "draw";
    return BEATS[you] === cpu ? "win" : "lose";
}

export default function JavascriptRPS() {
    const t = useTranslations();
    const reduce = useReducedMotion();

    const [scores, setScores] = useState({ you: 0, cpu: 0 });
    const [result, setResult] = useState(null); // { you, cpu, outcome }
    const [pendingYou, setPendingYou] = useState(null);
    const [rolling, setRolling] = useState(false);
    const [countdown, setCountdown] = useState(null); // 3, 2, 1 pendant le décompte
    const [roundId, setRoundId] = useState(0);
    const timerRef = useRef(null);

    useEffect(() => () => clearInterval(timerRef.current), []);

    function commit(you, cpu, outcome) {
        setResult({ you, cpu, outcome });
        setRoundId((n) => n + 1);
        setScores((prev) => ({
            you: prev.you + (outcome === "win" ? 1 : 0),
            cpu: prev.cpu + (outcome === "lose" ? 1 : 0),
        }));
    }

    function play(youId) {
        if (rolling) return;
        const cpuId = CHOICES[Math.floor(Math.random() * CHOICES.length)].id;
        const outcome = compute(youId, cpuId);

        if (reduce) {
            commit(youId, cpuId, outcome);
            return;
        }
        // Le coup du joueur s'affiche, puis l'ordinateur décompte 3, 2, 1
        // avant de révéler son coup et le résultat.
        setResult(null);
        setPendingYou(youId);
        setRolling(true);
        setCountdown(3);
        let n = 3;
        timerRef.current = setInterval(() => {
            n -= 1;
            if (n > 0) {
                setCountdown(n);
            } else {
                clearInterval(timerRef.current);
                timerRef.current = null;
                commit(youId, cpuId, outcome);
                setRolling(false);
                setPendingYou(null);
                setCountdown(null);
            }
        }, 600);
    }

    function reset() {
        clearInterval(timerRef.current);
        setScores({ you: 0, cpu: 0 });
        setResult(null);
        setPendingYou(null);
        setRolling(false);
        setCountdown(null);
    }

    const outcomeStyle = {
        win: "text-emerald-400",
        lose: "text-rose-400",
        draw: "text-amber-300",
    };

    const youEmoji = rolling
        ? EMOJI[pendingYou]
        : result
          ? EMOJI[result.you]
          : null;

    return (
        <section id="javascript-game" className="pt-16">
            <h2
                className="text-2xl dark:text-white font-bold font-sourceSans"
                style={{ marginBottom: "0.5em" }}
            >
                {t("Js_rps_title")}
            </h2>
            <div className="w-20 h-1 bg-primary rounded mt-3 mb-5"></div>

            <p className="text-black dark:text-lightBlue max-w-[700px] leading-7 font-geistSans mb-0">
                {t("Js_rps_intro")}
            </p>

            <div className="mt-10 relative rounded-3xl overflow-hidden bg-gradient-to-br from-bgDark-700 via-bgDark-800 to-bgDark-900 ring-1 ring-white/10">
                {/* Glows (teinte JS) */}
                <div className="absolute -top-24 -right-24 w-[460px] h-[460px] bg-amber-500/20 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute -bottom-24 -left-20 w-[380px] h-[380px] bg-yellow-500/10 rounded-full blur-3xl pointer-events-none" />

                {/* Barre de fenêtre */}
                <div className="relative flex items-center gap-2 px-5 py-3 border-b border-white/10">
                    <span className="w-3 h-3 rounded-full bg-red-400/90" />
                    <span className="w-3 h-3 rounded-full bg-yellow-400/90" />
                    <span className="w-3 h-3 rounded-full bg-green-400/90" />
                    <span className="ml-3 inline-flex items-center gap-1.5 text-[11px] font-mono text-gray-400">
                        <Icon
                            icon="logos:javascript"
                            className="w-3.5 h-3.5"
                            aria-hidden="true"
                        />
                        rps.js
                    </span>
                    <span className="ml-auto inline-flex items-center gap-1.5">
                        <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
                        </span>
                        <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-emerald-300">
                            {t("Js_rps_badge")}
                        </span>
                    </span>
                </div>

                <div className="relative p-6 sm:p-9 flex flex-col items-center">
                    {/* Score */}
                    <div className="flex items-center gap-6 font-mono text-sm mb-7">
                        <span className="text-gray-300">
                            {t("Js_rps_you")}{" "}
                            <b className="text-emerald-300 tabular-nums">
                                {scores.you}
                            </b>
                        </span>
                        <span className="text-gray-600">:</span>
                        <span className="text-gray-300">
                            <b className="text-rose-300 tabular-nums">
                                {scores.cpu}
                            </b>{" "}
                            {t("Js_rps_cpu")}
                        </span>
                    </div>

                    {/* Arène */}
                    <div className="w-full max-w-[460px] rounded-2xl bg-black/25 ring-1 ring-white/10 p-6 mb-7 min-h-[180px] flex flex-col items-center justify-center">
                        {!result && !rolling ? (
                            <div className="text-gray-400 text-center font-geistSans">
                                {t("Js_rps_prompt")}
                            </div>
                        ) : (
                            <div className="flex flex-col items-center gap-4">
                                <div className="flex items-center gap-6 sm:gap-10">
                                    {/* Vous */}
                                    <div className="flex flex-col items-center gap-2">
                                        <motion.span
                                            key={
                                                rolling
                                                    ? `you-${countdown}`
                                                    : `you-r-${roundId}`
                                            }
                                            initial={
                                                reduce
                                                    ? false
                                                    : rolling
                                                      ? { y: -10 }
                                                      : { scale: 0.6, opacity: 0 }
                                            }
                                            animate={
                                                rolling
                                                    ? { y: 0 }
                                                    : { scale: 1, opacity: 1 }
                                            }
                                            transition={{
                                                type: "spring",
                                                stiffness: 340,
                                                damping: 14,
                                            }}
                                            className="text-5xl sm:text-6xl"
                                        >
                                            {youEmoji}
                                        </motion.span>
                                        <span className="text-[11px] uppercase tracking-wider text-emerald-300">
                                            {t("Js_rps_you")}
                                        </span>
                                    </div>

                                    {/* Séparateur */}
                                    <span className="text-gray-500 font-mono text-sm">
                                        {t("Js_rps_vs")}
                                    </span>

                                    {/* Ordinateur */}
                                    <div className="flex flex-col items-center gap-2">
                                        <div className="h-[60px] sm:h-[72px] flex items-center justify-center">
                                            {rolling ? (
                                                <motion.span
                                                    key={countdown}
                                                    initial={{
                                                        scale: 1.6,
                                                        opacity: 0,
                                                    }}
                                                    animate={{
                                                        scale: 1,
                                                        opacity: 1,
                                                    }}
                                                    transition={{
                                                        type: "spring",
                                                        stiffness: 320,
                                                        damping: 16,
                                                    }}
                                                    className="text-5xl sm:text-6xl font-bold font-sourceSans text-amber-300 tabular-nums"
                                                >
                                                    {countdown}
                                                </motion.span>
                                            ) : (
                                                <motion.span
                                                    key={`cpu-r-${roundId}`}
                                                    initial={
                                                        reduce
                                                            ? false
                                                            : {
                                                                  scale: 0.6,
                                                                  opacity: 0,
                                                              }
                                                    }
                                                    animate={{
                                                        scale: 1,
                                                        opacity: 1,
                                                    }}
                                                    transition={{
                                                        type: "spring",
                                                        stiffness: 320,
                                                        damping: 16,
                                                    }}
                                                    className="text-5xl sm:text-6xl"
                                                >
                                                    {EMOJI[result.cpu]}
                                                </motion.span>
                                            )}
                                        </div>
                                        <span className="text-[11px] uppercase tracking-wider text-rose-300">
                                            {t("Js_rps_cpu")}
                                        </span>
                                    </div>
                                </div>
                                {!rolling && result && (
                                    <motion.div
                                        key={`out-${roundId}`}
                                        initial={
                                            reduce ? false : { opacity: 0, y: 6 }
                                        }
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.25 }}
                                        className={`text-lg font-bold font-sourceSans ${outcomeStyle[result.outcome]}`}
                                    >
                                        {t(`Js_rps_${result.outcome}`)}
                                    </motion.div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Boutons de coup */}
                    <div className="flex items-center gap-3 sm:gap-4">
                        {CHOICES.map((choice) => (
                            <button
                                key={choice.id}
                                type="button"
                                onClick={() => play(choice.id)}
                                disabled={rolling}
                                aria-label={t(choice.labelKey)}
                                className={`flex flex-col items-center gap-1.5 rounded-2xl bg-white/[0.06] hover:bg-amber-500/15 ring-1 ring-white/10 hover:ring-amber-400/40 disabled:opacity-50 disabled:hover:bg-white/[0.06] px-5 sm:px-7 py-3 transition active:scale-95 ${
                                    rolling && pendingYou === choice.id
                                        ? "ring-amber-400/60 bg-amber-500/15"
                                        : ""
                                }`}
                            >
                                <span className="text-3xl sm:text-4xl">
                                    {choice.emoji}
                                </span>
                                <span className="text-[11px] font-medium text-gray-300">
                                    {t(choice.labelKey)}
                                </span>
                            </button>
                        ))}
                    </div>

                    {/* Rejouer */}
                    {(result || scores.you > 0 || scores.cpu > 0) && (
                        <button
                            type="button"
                            onClick={reset}
                            className="mt-6 inline-flex items-center gap-2 rounded-full bg-white/[0.06] hover:bg-white/[0.1] ring-1 ring-white/10 px-4 py-2 text-sm text-gray-200 transition active:scale-95"
                        >
                            <SolarIcon
                                name="restart-bold-duotone"
                                className="w-4 h-4"
                            />
                            {t("Js_rps_reset")}
                        </button>
                    )}
                </div>
            </div>

            <p className="mt-4 text-sm text-black/60 dark:text-lightBlue/70 max-w-[700px] font-geistSans">
                {t("Js_rps_caption")}
            </p>
        </section>
    );
}
